import { captureException } from '@sentry/vue'
import { defineStore } from 'pinia'

import { MessagesLoadingError } from '@/app/errors/messages-loading-error'
import ChatRepository from '@/app/repositories/chat-repository'
import { PaginatedResponse } from '@/common/models/data/common/pagination/paginated-response'
import { ChatMessage, IChatMessage } from '@/common/models/data/dashboard/chat/chat-message'
import LangFilter from '@/app/util/lang-filter'
import { MessageSendError } from '@/app/errors/message-send-error'

const useChatStore = defineStore('chat', {
    state: () => ({
        connecting: false,
        loading: false,
        offline: false,
        data: new PaginatedResponse<ChatMessage>(),
        messages: [] as Array<ChatMessage>,
        nickname: '',
        message: ''
    }),
    actions: {
        async load (): Promise<void> {
            this.connecting = true

            try {
                const data = await ChatRepository.paginated(1)

                this.data.copyFrom(data)
                this.messages = data.data

                this.offline = false
                this.connecting = false
            } catch (e) {
                captureException(e, { level: 'error' })

                this.offline = true
                this.connecting = true
            }
        },
        async loadPrevious (): Promise<void> {
            if (this.loading) {
                return
            }

            if (this.data.meta.onLastPage) {
                return
            }

            try {
                const data = await ChatRepository.paginated(this.data.meta.nextPage)

                if (data.meta.total !== this.data.meta.total) {
                    this.messages = await ChatRepository.pages(this.data.meta.nextPage)
                } else {
                    this.messages = this.messages.concat(data.data)
                }

                this.data.copyFrom(data)
            } catch (e) {
                if (e instanceof Error) {
                    throw new MessagesLoadingError(e.message)
                }

                throw new MessagesLoadingError()
            } finally {
                this.loading = false
            }
        },
        async sendMessage (): Promise<void> {
            let success = false

            try {
                success = await ChatRepository.create({
                    author: this.nickname,
                    content: LangFilter.filter(this.message)
                })
            } catch (e) {
                if (e instanceof Error) {
                    throw new MessageSendError(e.message)
                }

                captureException(e, { level: 'error' })

                throw new MessageSendError()
            }

            if (!success) {
                throw new MessageSendError()
            }

            this.message = ''
        },
        prependMessage (message: IChatMessage): void {
            this.messages.unshift(new ChatMessage(message))
        }
    },
    persist: {
        paths: [
            'nickname'
        ]
    }
})

export {
    useChatStore
}
